import Link from "next/link";
import { FC, ReactNode } from "react";
import MediaBlock from "../blocks/MediaBlock";
import Button from "../Button";
import Typography from "../Typography";

type FeatureSectionProps = {
  h2: ReactNode;
  body: ReactNode;
  ctaLabel: string;
  ctaHref: string;
  videoSrc: string;
  videoAlt: string;
  imageSide: "left" | "right";
};

// Single alternating block — h2 + body + CTA on one side, looping
// product video on the other. Spec sections 8.1–8.5 of each tool page
// stack 3–6 of these. The CTA stays a button (not a VideoInput); per
// master spec §"Component library", VideoInput only appears in Hero
// and FinalCTA.
const FeatureSection: FC<FeatureSectionProps> = ({
  h2,
  body,
  ctaLabel,
  ctaHref,
  videoSrc,
  videoAlt,
  imageSide,
}) => {
  const flexDirection =
    imageSide === "right"
      ? "md:flex-row"
      : "md:flex-row-reverse";

  return (
    <section
      className={`max-w-[1500px] px-3 md:px-12 mx-auto flex flex-col ${flexDirection} mx-auto md:gap-16 gap-8 my-32 md:my-48 items-center`}
    >
      <div className="flex-1">
        <div className="flex flex-col gap-8 max-w-xl">
          <Typography variant="h2" component="h2">
            {h2}
          </Typography>
          <Typography variant="body1" className="text-slate-500 leading-relaxed">
            {body}
          </Typography>
          <div>
            <Link href={ctaHref}>
              <Button size="l">{ctaLabel}</Button>
            </Link>
          </div>
        </div>
      </div>
      <div className="flex-1 rounded-2xl w-full">
        <div className="bg-slate-150 pt-10 px-10 rounded-2xl">
          <MediaBlock
            data={{ type: "video", src: videoSrc, alt: videoAlt }}
            className="rounded-b-none rounded-t-lg mx-auto max-h-[480px]"
          />
        </div>
      </div>
    </section>
  );
};

export default FeatureSection;
